import { useMemo, useState, type ReactNode } from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";

type UserGroupSummary = {
  id: string;
  name: string;
  description?: string;
  color: string;
  platformIds: string[];
};

type UserGroupTableRow = {
  id: string;
  name: string;
  description: string;
  color: string;
  platformIds: string[];
  memberCount: number;
  platformsLabel: string;
};

type UserGroupsTableProps = {
  userGroups: UserGroupSummary[];
  userCountsByGroup: Map<string, number>;
  summarizePlatforms: (ids: string[]) => string;
  selectedUserGroupIds: string[];
  onSelectionChange: (ids: string[]) => void;
  toolbarActions?: ReactNode;
};

const UserGroupsTable = ({
  userGroups,
  userCountsByGroup,
  summarizePlatforms,
  selectedUserGroupIds,
  onSelectionChange,
  toolbarActions
}: UserGroupsTableProps) => {
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const normalizedSearch = useMemo(() => searchText.trim().toLowerCase(), [searchText]);

  const allRows = useMemo<UserGroupTableRow[]>(
    () =>
      userGroups.map((group) => ({
        id: group.id,
        name: group.name,
        description: group.description?.trim() ?? "",
        color: group.color,
        platformIds: group.platformIds,
        memberCount: userCountsByGroup.get(group.id) ?? 0,
        platformsLabel: summarizePlatforms(group.platformIds)
      })),
    [summarizePlatforms, userCountsByGroup, userGroups]
  );

  const rows = useMemo(() => {
    if (!normalizedSearch) {
      return allRows;
    }

    return allRows.filter((row) => {
      const values = [row.name, row.description, row.platformsLabel, String(row.memberCount)];
      return values.some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [allRows, normalizedSearch]);

  const selectionModel = useMemo(
    () => ({ type: "include", ids: new Set(selectedUserGroupIds) } as const),
    [selectedUserGroupIds]
  );

  const columns = useMemo<GridColDef<UserGroupTableRow>[]>(() => {
    const allColumns: GridColDef<UserGroupTableRow>[] = [
      {
        field: "name",
        headerName: "Group",
        flex: 1,
        minWidth: 180,
        resizable: true,
        renderCell: (params) => (
          <Stack spacing={0.25} sx={{ py: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {params.row.name}
            </Typography>
            {params.row.description ? (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "normal" }}>
                {params.row.description}
              </Typography>
            ) : null}
          </Stack>
        )
      },
      {
        field: "memberCount",
        headerName: "Users",
        flex: 0.45,
        minWidth: 96,
        resizable: true,
        renderCell: (params) => (
          <Chip
            icon={<PeopleIcon fontSize="small" />}
            label={params.row.memberCount}
            size="small"
            variant="outlined"
            sx={{
              "& .MuiChip-icon": {
                color: params.row.color
              }
            }}
          />
        )
      },
      {
        field: "platformsLabel",
        headerName: "Platforms",
        flex: 1.2,
        minWidth: 220,
        resizable: true,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={params.row.platformsLabel} placement="top" describeChild>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {params.row.platformsLabel}
            </Typography>
          </Tooltip>
        )
      },
      {
        field: "color",
        headerName: "Color",
        flex: 0.45,
        minWidth: 108,
        resizable: true,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              component="span"
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: params.row.color,
                border: "1px solid",
                borderColor: "divider"
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
              {params.row.color}
            </Typography>
          </Stack>
        )
      }
    ];

    if (isSmallScreen) {
      const allowedFields = new Set(["name", "memberCount"]);
      return allColumns.filter((column) => allowedFields.has(column.field));
    }

    return allColumns;
  }, [isSmallScreen]);

  return (
    <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent={{ xs: "flex-start", sm: "space-between" }}
        gap={1}
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        sx={{ minWidth: 0 }}
      >
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search groups"
          size="small"
          fullWidth
          sx={{
            minWidth: { xs: "100%", sm: 140 },
            maxWidth: { sm: 280, md: 260 },
            flex: 1
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        {toolbarActions ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              gap: 0.5,
              width: { xs: "100%", sm: "auto" },
              flexWrap: "wrap"
            }}
          >
            {toolbarActions}
          </Box>
        ) : null}
      </Stack>
      <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
        <DataGrid
          autoHeight
          density="compact"
          hideFooterSelectedRowCount
          checkboxSelection
          rows={rows}
          columns={columns}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => {
            let ids: string[] = [];
            if (model && typeof model === "object" && "ids" in model) {
              const idSet = (model as { ids?: Set<unknown> | Iterable<unknown> }).ids;
              const normalized = idSet instanceof Set ? Array.from(idSet) : Array.from(idSet ?? []);
              ids = normalized.map(String);
            } else if (Array.isArray(model)) {
              ids = (model as unknown[]).map(String);
            }
            onSelectionChange(ids);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 }
            }
          }}
          disableRowSelectionOnClick={false}
          sx={{
            minWidth: 420,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              py: 0.75
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default UserGroupsTable;
